import { Map as MapIcon, Search } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import FieldCard from '/@/components/field-info-v2/field-card';
import FieldSkeleton from '/@/components/field-skeleton';
import MapComponent from '/@/components/map';
import Markdown from '/@/components/markdown';
import { getCenterOfGeometry } from '/@/core/fields';
import type { Geometry } from '/@/core/fields/geometry';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFieldViewFields, useImportFieldViewFieldsMutation } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field, FieldViewFieldItem } from '/@/types/field';

const BATCH_SIZE = 50;

const getFieldViewId = (field: FieldViewFieldItem): string =>
    String((field.fieldviewData as { id?: string })?.id ?? '');

function fieldViewItemToMapField(field: FieldViewFieldItem): Field {
    const props = field.fieldviewData as {
        id?: string;
        properties?: { name?: string; area?: { v?: number } };
        geometry?: { type?: string; coordinates?: unknown };
    };
    const fvId = getFieldViewId(field);
    const geometry = (props?.geometry as { coordinates?: unknown })?.coordinates ?? [];
    const geom = geometry as Geometry;
    const [lng, lat] = getCenterOfGeometry(geom);
    const areaHa = props?.properties?.area?.v ?? 0;
    const areaM2 = areaHa * 10000;
    return {
        id: 0,
        _id: `fieldview-${fvId}`,
        name: props?.properties?.name ?? '',
        geometry: geom,
        lat,
        lng,
        area: areaM2,
        iconType: 'fieldview',
    } as unknown as Field;
}

export interface ImportFieldsProps {
    open: boolean;
    onClose: () => void;
    bodyProps?: React.ComponentProps<typeof ModalV2>['bodyProps'];
    actionsProps?: React.ComponentProps<typeof ModalV2>['actionsProps'];
    /** FieldView account id; used to fetch fields and trigger the import API. */
    fieldviewId: string | number;
    /** Called after a successful import (e.g. to close the modal). */
    onImportSuccess?: () => void;
}

const ImportFields = ({ open, onClose, bodyProps, actionsProps, fieldviewId, onImportSuccess }: ImportFieldsProps) => {
    const country = useSelector(getSessionCountry);
    const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [fieldForMap, setFieldForMap] = useState<FieldViewFieldItem | null>(null);
    const [importError, setImportError] = useState<unknown>(null);
    const mapRef = useRef<{ fitPoints: (geom: Geometry, maxZoom?: number, useEventListener?: boolean) => void } | null>(
        null
    );

    const fieldviewFieldsQuery = useFieldViewFields(
        { fieldviewId: Number(fieldviewId) || undefined },
        { enabled: open && !!fieldviewId }
    );
    const importFieldViewFieldsMutation = useImportFieldViewFieldsMutation();

    const results = fieldviewFieldsQuery.data?.results ?? [];
    const searchLower = searchQuery.trim().toLowerCase();
    const filteredResults = useMemo(
        () =>
            searchLower
                ? results.filter((f) => {
                      const props = (f.fieldviewData as { properties?: { name?: string } })?.properties;
                      const name = (props?.name ?? '').toLowerCase();
                      const location = (f.locationData?.fullLocationName ?? '').toLowerCase();
                      return name.includes(searchLower) || location.includes(searchLower);
                  })
                : results,
        [results, searchLower]
    );
    const importableFields = useMemo(() => results.filter((f) => !f.id) as FieldViewFieldItem[], [results]);
    const importableIds = useMemo(() => new Set(importableFields.map(getFieldViewId)), [importableFields]);
    const allImportableSelected =
        importableIds.size > 0 &&
        importableIds.size === importableFields.filter((f) => selectedFieldIds.has(getFieldViewId(f))).length;

    const toggleSelection = useCallback((id: string, isAlreadyImported: boolean) => {
        if (isAlreadyImported) return;
        setSelectedFieldIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const selectAllImportable = useCallback(() => {
        setSelectedFieldIds(new Set(importableIds));
    }, [importableIds]);

    const deselectAll = useCallback(() => {
        setSelectedFieldIds(new Set());
    }, []);

    const handleImportClick = useCallback(async () => {
        setImportError(null);
        const selected = results.filter((f) => selectedFieldIds.has(getFieldViewId(f)));
        if (selected.length === 0) return;
        const ids = selected.map(getFieldViewId);
        const batches: string[][] = [];
        for (let i = 0; i < ids.length; i += BATCH_SIZE) {
            batches.push(ids.slice(i, i + BATCH_SIZE));
        }
        try {
            for (const batch of batches) {
                await importFieldViewFieldsMutation.trigger({
                    fieldviewId: String(fieldviewId),
                    fieldviewFieldIds: batch,
                });
            }
            onImportSuccess?.();
        } catch (err) {
            setImportError(err);
        }
    }, [results, selectedFieldIds, importFieldViewFieldsMutation.trigger, fieldviewId, onImportSuccess]);

    const mapField = fieldForMap ? fieldViewItemToMapField(fieldForMap) : null;

    const debouncedFieldForMap = useDebounce(fieldForMap, 100);
    useEffect(() => {
        if (!debouncedFieldForMap || !mapRef.current) return;
        const props = debouncedFieldForMap.fieldviewData as { geometry?: { coordinates?: unknown } };
        const geometry = (props?.geometry?.coordinates ?? []) as Geometry;
        if (!Array.isArray(geometry) || geometry.length === 0) return;
        const t = setTimeout(() => {
            mapRef.current?.fitPoints(geometry, 14, false);
        }, 150);
        return () => clearTimeout(t);
    }, [debouncedFieldForMap]);

    return (
        <>
            <ModalV2
                open={open}
                onClose={onClose}
                title={_('Importar lotes desde FieldView')}
                actions={
                    <>
                        <Typography variant='subtitle1' color='primary.mui.inactive'>
                            {_('{{count}} lote seleccionado', { count: selectedFieldIds.size })}
                        </Typography>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={handleImportClick}
                            disabled={selectedFieldIds.size === 0 || importFieldViewFieldsMutation.isMutating}
                        >
                            {importFieldViewFieldsMutation.isMutating ? _('Importando...') : _('Importar lotes')}
                        </Button>
                    </>
                }
                bodyProps={{ ...bodyProps, alignItems: 'flex-start', textAlign: 'left' }}
                actionsProps={{
                    ...actionsProps,
                    sx: { ...actionsProps?.sx, justifyContent: 'space-between', width: '100%', alignItems: 'center' },
                }}
                maxWidth='md'
            >
                <Alert
                    severity='blue'
                    sx={{
                        textAlign: 'start',
                    }}
                >
                    <Markdown>
                        {_(
                            'Tené en cuenta que solo importamos los **lotes que pertenecen a tu operación** de FieldView.'
                        )}
                    </Markdown>
                </Alert>
                <Typography variant='subtitle1' color='primary'>
                    {_(
                        'Selecciona los lotes que deseas importar a tu cuenta. Los lotes ya importados aparecen deshabilitados.'
                    )}
                </Typography>
                {importError && (
                    <Alert
                        severity='danger'
                        onClose={() => {
                            setImportError(null);
                        }}
                    >
                        {_('Error al importar los lotes. Intentá nuevamente.')}
                    </Alert>
                )}
                <TextField
                    placeholder={_('Buscar por nombre del lote')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    color='secondary'
                    sx={{ mt: 1, mb: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <Search fontSize='small' />
                            </InputAdornment>
                        ),
                    }}
                />
                {importableIds.size > 0 && (
                    <Stack direction='row' spacing={2} sx={{ flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allImportableSelected}
                                    indeterminate={selectedFieldIds.size > 0 && !allImportableSelected}
                                    onChange={() => (allImportableSelected ? deselectAll() : selectAllImportable())}
                                    color='secondary'
                                />
                            }
                            label={
                                <Typography color='secondary' variant='button'>
                                    {_('Seleccionar todos ({{count}} lote disponible)', {
                                        count: importableIds.size,
                                    })}
                                </Typography>
                            }
                        />
                    </Stack>
                )}
                {!fieldviewFieldsQuery.isLoading && results.length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                        {_('No hay lotes en esta cuenta de FieldView.')}
                    </Typography>
                )}
                {fieldviewFieldsQuery.isLoading && (
                    <Stack divider={<Divider />} width='100%' height='100%' overflow='auto'>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <FieldSkeleton key={`skeleton-fieldview-${index}`} />
                        ))}
                    </Stack>
                )}
                {!fieldviewFieldsQuery.isLoading && results.length > 0 && (
                    <Stack divider={<Divider />} width='100%' height='100%' overflow='auto'>
                        {filteredResults.length === 0 ? (
                            <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
                                {_('Ningún lote coincide con la búsqueda.')}
                            </Typography>
                        ) : (
                            filteredResults.map((field) => {
                                const fvId = getFieldViewId(field);
                                const isAlreadyImported = !!field.id;
                                const props = field.fieldviewData as {
                                    id?: string;
                                    properties?: { name?: string; area?: { v?: number } };
                                    geometry?: { type?: string; coordinates?: unknown };
                                };
                                const name = props?.properties?.name ?? '';
                                const areaHa = props?.properties?.area?.v ?? 0;
                                const areaM2 = areaHa * 10000;
                                const geometry = props?.geometry ?? {};
                                const coordinates = geometry.coordinates || [];
                                return (
                                    <Stack
                                        key={fvId}
                                        sx={[
                                            isAlreadyImported
                                                ? {
                                                      filter: 'grayscale(0.7)',
                                                      background: (t: Theme) => t.palette.grey[100],
                                                      opacity: 0.8,
                                                  }
                                                : {
                                                      cursor: 'pointer',
                                                  },
                                        ]}
                                        direction='row'
                                        p={2}
                                        spacing={2}
                                        alignItems='center'
                                        onClick={() => toggleSelection(fvId, isAlreadyImported)}
                                    >
                                        <Checkbox
                                            checked={selectedFieldIds.has(fvId)}
                                            disabled={isAlreadyImported}
                                            size='medium'
                                        />
                                        <FieldCard
                                            name={name}
                                            geometry={coordinates as Geometry}
                                            area={areaM2}
                                            location={field.locationData?.fullLocationName ?? ''}
                                            isFieldview
                                        />
                                        <IconButton
                                            size='small'
                                            color='secondary'
                                            aria-label={_('Ver en Mapa')}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                setFieldForMap(field);
                                            }}
                                            sx={{ ml: 'auto !important' }}
                                        >
                                            <MapIcon fontSize='small' />
                                        </IconButton>
                                    </Stack>
                                );
                            })
                        )}
                    </Stack>
                )}
            </ModalV2>
            {fieldForMap && mapField && (
                <ModalV2
                    open
                    onClose={() => setFieldForMap(null)}
                    title={_('Ver en Mapa')}
                    maxWidth='md'
                    actions={
                        <Box
                            sx={{
                                background: 'white',
                                zIndex: '1',
                            }}
                        >
                            <Button onClick={() => setFieldForMap(null)} variant='text' color='secondary'>
                                {_('Cerrar')}
                            </Button>
                        </Box>
                    }
                >
                    <Box sx={{ height: 400, width: '100%', overflow: 'hidden' }}>
                        <MapComponent
                            ref={mapRef}
                            fields={[mapField]}
                            country={country?.code ?? 'ar'}
                            overlay={undefined}
                            offline={false}
                            noGeocoder
                            simplifyPolygons
                            zoomControls
                            onClick={() => {}}
                            onFieldClick={() => {}}
                            onOverlayChange={() => {}}
                            trackEvent={() => {}}
                            radius={false}
                            onOverlayClose={() => {}}
                            expand={() => {}}
                            showOverlayCard={false}
                            getMarkerIcon={() => 'fieldview'}
                            zoomPosition='TOP_RIGHT'
                            isLoading={false}
                        >
                            {null}
                        </MapComponent>
                    </Box>
                </ModalV2>
            )}
        </>
    );
};

export default ImportFields;
